import { useQuery } from "@tanstack/react-query";
import type { FileEntry } from "./types";
import { apiGet, apiPost, apiDelete } from "./api";
import { useSession } from "./auth";
import { queryClient } from "./queryClient";

export interface ServerFile {
  id: string; name: string; mime: string; size_bytes: number; uploaded_by: string;
  uploaded_at: number; room_id?: string | null; folder_id?: string | null;
}
export function toClientFile(file: ServerFile): FileEntry {
  return { id: file.id, name: file.name, size: file.size_bytes, mime: file.mime,
    uploaderId: file.uploaded_by, uploadedAt: new Date(file.uploaded_at).toISOString(),
    roomId: file.room_id ?? undefined, folderId: file.folder_id ?? undefined };
}
export async function uploadFile(file: File, roomId?: string): Promise<FileEntry> {
  const mime = file.type || "application/octet-stream";
  const upload = await apiPost<{ upload_url: string; upload_session_id: string; signed_headers: Record<string, string>; r2_key: string }>("/v1/files/presigned-upload", {
    filename: file.name, mime, size_bytes: file.size, room_id: roomId,
  });
  // Only the signed upload headers go to object storage, never the app bearer token.
  const response = await fetch(upload.upload_url, { method: "PUT", headers: upload.signed_headers, body: file });
  if (!response.ok) throw new Error(`アップロードに失敗しました (${response.status})`);
  const completed = await apiPost<{ file: ServerFile }>("/v1/files/finalize", {
    upload_session_id: upload.upload_session_id, r2_key: upload.r2_key,
    filename: file.name, mime, size_bytes: file.size, room_id: roomId,
  });
  await queryClient.invalidateQueries({ queryKey: ["files"] });
  return toClientFile(completed.file);
}
export async function deleteFile(id: string): Promise<void> {
  await apiDelete(`/v1/files/${encodeURIComponent(id)}`);
  await queryClient.invalidateQueries({ queryKey: ["files"] });
}
export async function fileDownloadUrl(id: string, attachment = false): Promise<string> {
  const result = await apiGet<{ download_url?: string; url?: string }>(`/v1/files/${encodeURIComponent(id)}/download-url${attachment ? "?attachment=1" : ""}`);
  const url = result.download_url ?? result.url;
  if (!url) throw new Error("ダウンロードリンクを取得できませんでした");
  return url;
}
export async function downloadFile(file: FileEntry): Promise<void> {
  const url = await fileDownloadUrl(file.id, true);
  const anchor = document.createElement("a");
  anchor.href = url; anchor.download = file.name; anchor.rel = "noopener";
  anchor.target = "_blank"; document.body.appendChild(anchor); anchor.click(); anchor.remove();
}
export function createFileShare(fileId: string, hours: number, password?: string) {
  return apiPost<{ id: string; share_url: string; expires_at: number; charge_jpy: number }>("/v1/guest-shares", {
    file_id: fileId, expires_in_hours: hours, password: password || undefined,
  });
}
export interface ActiveFileShare {
  id: string; created_at: number; expires_at: number; has_password: boolean;
  created_by: string; created_by_name: string | null; download_limit: number | null;
  downloaded: number; can_revoke: boolean;
}
export async function listFileShares(fileId: string): Promise<ActiveFileShare[]> {
  const result = await apiGet<{ shares: ActiveFileShare[] }>(`/v1/guest-shares?file_id=${encodeURIComponent(fileId)}`);
  return result.shares;
}
export async function revokeFileShare(shareId: string): Promise<void> {
  await apiDelete(`/v1/guest-shares/${encodeURIComponent(shareId)}`);
}
export function useFiles() {
  const session = useSession();
  const query = useQuery({ queryKey: ["files", session?.user.id], enabled: !!session,
    queryFn: async () => {
      const files: ServerFile[] = [];
      let cursor: string | null | undefined;
      do {
        const result: { files: ServerFile[]; next_cursor?: string | null } = await apiGet(`/v1/files?limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`);
        files.push(...result.files);
        if (cursor && result.next_cursor === cursor) throw new Error("ファイル一覧のページ情報が更新されませんでした");
        cursor = result.next_cursor;
      } while (cursor);
      return files.map(toClientFile);
    } });
  return { files: query.data ?? [], loading: query.isPending, error: query.error,
    isFallback: false, refresh: query.refetch };
}
